import * as DM from '@radix-ui/react-dropdown-menu';
import {
  BookOpen,
  ChevronDown,
  Clock,
  Smartphone,
  ChevronsLeft,
  ChevronsRight,
  FileSignature,
  FileText,
  HandCoins,
  CalendarDays,
  Fuel,
  HardHat,
  Home,
  IdCard,
  KeyRound,
  ListTodo,
  LogOut,
  Menu,
  Monitor,
  Moon,
  Newspaper,
  PackageCheck,
  Search,
  Sun,
  Users,
  X,
  Zap,
  Truck,
  UserRound,
  Globe,
  ListChecks,
  UserPlus,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { Permission } from '@mt/shared';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Logo } from '@/components/brand/Logo';
import { ErrorBoundary } from '@/components/ui/error-boundary';
import { EmptyState } from '@/components/ui/states';
import { THEME_LABELS, useTheme, type ThemeMode } from '@/design-system/theme';
import { useFollowUpCounts } from '@/features/customers/api';
import { DriverTasksHeaderButton } from '@/features/fleet/DriverTasksCard';
import { NotificationBell } from '@/features/notifications/NotificationBell';
import { InstallAppDialog } from '@/features/pwa/InstallAppDialog';
import { OfflineBanner } from '@/features/pwa/OfflineBanner';
import { useSiteQueueSync } from '@/features/project-sites/offline-queue';
import { useSwNavigate } from '@/features/pwa/useSwNavigate';
import { ChangePasswordDialog } from '@/features/users/ChangePasswordDialog';
import { MyProfileDialog } from '@/features/users/MyProfileDialog';
import { InboxDrawer } from '@/features/zalo/InboxDrawer';
import { useAuth } from '@/lib/auth';
import { useHotkeys } from '@/lib/hotkeys';
import { useIsMobile } from '@/lib/media';
import { menuAllowed } from '@/lib/menu-access';
import { useInstallMode } from '@/lib/pwa';
import { cn } from '@/lib/utils';
import { GlobalSearch } from './GlobalSearch';
import { usePickOpenCount } from '@/features/stock-docs/pickCount';
import { PickSearchBox } from '@/features/stock-docs/PickSearchBox';
import { isGroup, NAV, type NavDataKey, type NavEntry, type NavGroup, type NavLeaf } from './nav';
import { useEmptyNavData } from './navData';
import { isCtvOnly, isPickerOnly, isStaffOnly, usesSalesBar } from '@/lib/roles';
import { useEssSummary } from '@/features/hr/ess-api';
import { useAutoCheckIn } from '@/features/hr/useAutoCheckIn';
import { visibleSections } from '@/features/pwa/launcher-sections';

const ROLE_LABELS: Record<string, string> = {
  admin: 'Quản trị hệ thống',
  sales_manager: 'Trưởng phòng kinh doanh',
  sales: 'Nhân viên kinh doanh',
  purchasing: 'Nhân viên mua hàng',
  accountant: 'Kế toán',
};

/**
 * `path` = pathname + search. Mục trỏ tới tab (`/doi-xe?tab=…`) chỉ sáng khi đúng tab;
 * mục thường bỏ qua chuỗi truy vấn, trừ khi trang đang mở một tab có mục riêng trong menu.
 */
const TAB_LEAVES = NAV.flatMap((e) => (isGroup(e) ? e.children : [e])).filter((l) => l.to.includes('?'));
const tabOf = (to: string) => new URLSearchParams(to.split('?')[1]).get('tab');
const isLeafActive = (l: NavLeaf, path: string) => {
  const [pathname = '', search = ''] = path.split('?');
  const tab = new URLSearchParams(search).get('tab');
  if (l.to.includes('?')) return pathname === l.to.split('?')[0] && tab === tabOf(l.to);
  if (pathname === l.to && tab && TAB_LEAVES.some((t) => t.to.split('?')[0] === l.to && tabOf(t.to) === tab)) return false;
  return pathname === l.to || (!l.end && pathname.startsWith(l.to + '/')) || !!l.match?.test(pathname);
};

const GROUPS_KEY = 'mt.nav.open';
const COLLAPSED_KEY = 'mt.sidebar.collapsed';
const THEME_ICONS: Record<ThemeMode, typeof Sun> = { light: Sun, dark: Moon, system: Monitor };

function loadCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}
function loadOpenGroups(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(GROUPS_KEY) ?? '{}') as Record<string, boolean>;
  } catch {
    return {};
  }
}

export function AppShell() {
  const { user, can, logout } = useAuth();
  useSiteQueueSync();
  // Mở app ở bất kỳ màn nào: đủ điều kiện (WiFi công ty + GPS trong cơ sở) thì tự chấm vào
  useAutoCheckIn(!!user);
  const { pathname, search } = useLocation();
  // Giống HomeRedirect (App.tsx): tài khoản chỉ có vai trò Tài xế
  const driverOnly = can('fleet.driver') && !can('fleet.read') && !can('customer.read') && !can('quote.read');
  // Giống HomeRedirect: tài khoản chỉ chấm công → menu và thanh dưới chỉ còn Chấm công / Của tôi / Hướng dẫn
  const staffOnly = isStaffOnly(user?.roles, user?.permissions);
  // Phụ kho (anh Tiến 14/10/2026): chỉ Soạn hàng + Chấm công + Của tôi, không có Menu
  const pickerOnly = isPickerOnly(user?.roles, user?.permissions);
  const here = pathname + search;
  // Điện thoại gọn (anh Tiến 14/10/2026): bỏ mục chưa có dữ liệu; Hướng dẫn lên đầu trang cạnh chuông
  const isMobile = useIsMobile();
  // Ô tìm chỉ tìm sản phẩm, khách, báo giá: ẩn với người không xem được mục nào
  const canSearch = can('product.read') || can('customer.read') || can('quote.read');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [installOpen, setInstallOpen] = useState(false);
  const installMode = useInstallMode();
  const [searchOpen, setSearchOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(loadOpenGroups);
  // Sidebar thu gọn chỉ áp dụng màn hình lớn; menu điện thoại luôn mở rộng
  const [collapsed, setCollapsed] = useState(loadCollapsed);
  const { mode: themeMode, setMode: setThemeMode } = useTheme();
  const counts = useFollowUpCounts(can('customer.read'));
  const due = (counts.data?.overdue ?? 0) + (counts.data?.today ?? 0);

  useHotkeys({ 'ctrl+k': () => setSearchOpen(true) }, !searchOpen);
  useSwNavigate();
  useEffect(() => setMobileOpen(false), [pathname]);

  // Lọc theo quyền; nhóm không còn mục nào thì ẩn.
  // Anh Tiến 20/10/2026: mục không có quyền, không có dữ liệu thì ẩn hẳn: bỏ mục "sắp có", mục danh sách rỗng (hideIfEmpty).
  // DEC-446: thêm quyền xem từng mục menu (menu.*)
  const functional = (l: NavLeaf) => (l.anyPerm ? l.anyPerm.some((p) => can(p)) : !l.perm || can(l.perm));
  const permitted = (l: NavLeaf) => functional(l) && menuAllowed(can, l.to);
  const dataKeys = useMemo(
    () =>
      staffOnly || pickerOnly
        ? []
        : NAV.flatMap((e) => (isGroup(e) ? e.children : [e])).flatMap((l): NavDataKey[] => (l.hideIfEmpty && permitted(l) ? [l.hideIfEmpty] : [])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [can, staffOnly, pickerOnly],
  );
  const emptyData = useEmptyNavData(dataKeys);
  const entries = useMemo(() => {
    if (staffOnly) return STAFF_NAV;
    if (pickerOnly) return PICKER_NAV;
    const ctvOnly = isCtvOnly(user?.roles);
    const empty = new Set(emptyData.split(','));
    const allowed = (l: NavLeaf) =>
      !(l.staffOnly && ctvOnly) &&
      !(l.ctvOnly && !ctvOnly) &&
      !l.soon &&
      !(isMobile && l.to === '/huong-dan') &&
      !(l.hideIfEmpty && empty.has(l.hideIfEmpty) && !isLeafActive(l, here)) &&
      permitted(l);
    return NAV.map((e): NavEntry | null => {
      if (!isGroup(e)) return allowed(e) ? e : null;
      const children = e.children.filter(allowed);
      return children.length ? { ...e, children } : null;
    }).filter((e): e is NavEntry => !!e);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [can, user?.roles, staffOnly, pickerOnly, isMobile, emptyData, here]);

  // DEC-446: mở thẳng đường dẫn của mục menu bị bỏ tick (có quyền nghiệp vụ nhưng không có quyền menu) → báo không có quyền.
  // Người không có quyền nghiệp vụ giữ như trước (trang tự xử lý), nên không ai bị chặn thêm khi chưa chỉnh quyền menu.
  const menuBlocked = useMemo(() => {
    const active = NAV.flatMap((e) => (isGroup(e) ? e.children : [e])).filter((l) => isLeafActive(l, here) && functional(l));
    return active.length > 0 && active.every((l) => !menuAllowed(can, l.to));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [can, here]);

  // Điện thoại (anh Tiến 14/10/2026): mục menu chưa có ô ở màn mở đầu / thanh dưới. Không còn mục nào → bỏ nút Menu;
  // chỉ còn 1 mục → đưa mục đó ra thanh dưới thay cho nút Menu.
  // Anh Tiến 02/11/2026: Kinh doanh / Mua hàng / Trưởng bộ phận / Giám đốc khu vực có chân trang 5 mục riêng (SALES_TABS)
  const salesBar = !staffOnly && !pickerOnly && !driverOnly && usesSalesBar(user?.roles, can);
  const mobileTabs = (isCtvOnly(user?.roles) ? CTV_TABS : salesBar ? SALES_TABS : MOBILE_TABS).filter(
    (t) => (!t.perm || can(t.perm)) && (!t.anyPerm || t.anyPerm.some((p) => can(p))) && menuAllowed(can, t.to) && !(t.notPerm && can(t.notPerm)) && (!t.role || !!user?.roles?.includes(t.role)) && !(t.notRole && user?.roles?.includes(t.notRole)),
  );
  const pickCount = usePickOpenCount(isMobile && (pickerOnly || mobileTabs.some((t) => t.to === '/soan-hang')));
  const menuExtras = useMemo(() => {
    if (!isMobile || staffOnly || pickerOnly || driverOnly) return null;
    const path = (to: string) => to.split('?')[0];
    const covered = new Set([
      '/tong-quan',
      '/cham-cong',
      '/cong-nhan-vien',
      ...mobileTabs.map((t) => t.to),
      ...visibleSections(can, isCtvOnly(user?.roles)).flatMap((sec) => sec.tiles.flatMap((t) => (t.to ? [path(t.to)] : []))),
    ]);
    const leaves = entries.flatMap((e) => (isGroup(e) ? e.children : [e]));
    return leaves.filter((l, i) => !covered.has(path(l.to)) && leaves.findIndex((x) => path(x.to) === path(l.to)) === i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, staffOnly, pickerOnly, driverOnly, entries, can, user?.roles]);

  // Mỗi lần chỉ mở 1 nhóm: mở nhóm này thì đóng các nhóm khác
  const toggleGroup = (key: string, open: boolean) =>
    setOpenGroups((g) => {
      const next = open ? Object.fromEntries(Object.keys(g).map((k) => [k, false])) : { ...g };
      next[key] = open;
      try {
        localStorage.setItem(GROUPS_KEY, JSON.stringify(next));
      } catch {
        /* trình duyệt chặn lưu – bỏ qua */
      }
      return next;
    });

  const toggleCollapsed = () =>
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, c ? '0' : '1');
      } catch {
        /* trình duyệt chặn lưu – bỏ qua */
      }
      return !c;
    });

  // Ở chế độ thu gọn, bấm biểu tượng nhóm thì mở rộng sidebar và mở nhóm đó
  const openGroupFromRail = (key: string) => {
    toggleCollapsed();
    toggleGroup(key, true);
  };

  const renderSidebar = (rail: boolean) => (
    <>
      <div className={cn('flex h-16 shrink-0 items-center border-b border-white/10', rail ? 'justify-center px-2' : 'px-3')}>
        <Logo sub="PCCC – THÉP – T.B.I CÔNG NGHIỆP" compact={rail} className="[&_img]:size-10" />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto [scrollbar-width:thin]">
        <nav className={cn('flex flex-col gap-1 py-3', rail ? 'items-center px-2' : 'px-2')} aria-label="Menu chính">
          {entries.map((e) =>
            isGroup(e) ? (
              rail ? (
                <RailButton
                  key={e.key}
                  icon={e.icon}
                  label={e.label}
                  active={e.children.some((c) => isLeafActive(c, here))}
                  onClick={() => openGroupFromRail(e.key)}
                />
              ) : (
                <Group key={e.key} group={e} path={here} due={due} overdue={!!counts.data?.overdue} open={openGroups[e.key]} onToggle={toggleGroup} />
              )
            ) : (
              <TopLink key={e.to} leaf={e} active={isLeafActive(e, here)} rail={rail} />
            ),
          )}
        </nav>
      </div>
    </>
  );

  return (
    <div className="flex h-full">
      <aside
        className={cn(
          'hidden shrink-0 flex-col bg-sidebar text-sidebar-foreground transition-[width] lg:flex',
          collapsed ? 'w-(--sidebar-width-collapsed)' : 'w-(--sidebar-width)',
        )}
      >
        {renderSidebar(collapsed)}
        <button
          onClick={toggleCollapsed}
          className={cn(
            'flex h-10 shrink-0 items-center gap-2 border-t border-white/10 text-sm text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-foreground',
            collapsed ? 'justify-center' : 'px-4',
          )}
          aria-label={collapsed ? 'Mở rộng menu' : 'Thu gọn menu'}
          title={collapsed ? 'Mở rộng menu' : 'Thu gọn menu'}
        >
          {collapsed ? <ChevronsRight className="size-4" /> : <ChevronsLeft className="size-4" />}
          {!collapsed && <span>Thu gọn</span>}
        </button>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-0 bg-overlay" />
          <aside
            className="absolute top-0 left-0 flex h-full w-(--sidebar-width) flex-col bg-sidebar text-sidebar-foreground shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={() => setMobileOpen(false)} aria-label="Đóng menu" className="absolute top-5 right-2 z-10 text-sidebar-muted">
              <X className="size-5" />
            </button>
            {renderSidebar(false)}
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 shrink-0 items-center gap-3 border-b bg-card px-3 lg:px-5">
          <button className="hidden md:block lg:hidden" onClick={() => setMobileOpen(true)} aria-label="Mở menu">
            <Menu className="size-5" />
          </button>
          {/* Anh Tiến 26/10/2026: màn Soạn hàng – ô trên cùng tìm đơn (lọc thẻ soạn), không phải tìm sản phẩm */}
          {pathname === '/soan-hang' ? (
            <PickSearchBox />
          ) : canSearch ? (
            <button
              onClick={() => setSearchOpen(true)}
              className="flex h-10 min-w-0 flex-1 items-center sm:max-w-lg gap-2 rounded-lg border bg-muted/60 px-3 text-left text-sm text-muted-foreground transition hover:border-input hover:bg-card"
            >
              <Search className="size-4 shrink-0" />
              <span className="flex-1 truncate">Tìm sản phẩm, mã hàng, khách hàng, báo giá...</span>
              <kbd className="hidden rounded border bg-card px-1.5 py-0.5 font-mono text-[11px] sm:inline">Ctrl + K</kbd>
            </button>
          ) : driverOnly ? (
            // Anh Tiến 01/11/2026: tài xế có nút "Việc được giao" trên cùng (chỗ trống đầu trang), nhãn đỏ "Gấp" khi có việc Khẩn / Quá hạn
            <div className="flex min-w-0 flex-1 items-center">
              <DriverTasksHeaderButton />
            </div>
          ) : (
            <div className="flex-1" />
          )}

          <div className={cn('ml-auto flex shrink-0 items-center', driverOnly ? 'gap-1' : 'gap-3')}>
            {/* Chân trang Kinh doanh (anh Tiến 02/11/2026): đã có ô Việc của tôi ở dưới → trên điện thoại chỗ này thành nút Trang chủ */}
            {salesBar && (
              <Link
                to="/ung-dung"
                className="rounded-full p-2 text-muted-foreground hover:bg-muted hover:text-foreground md:hidden"
                title="Trang chủ"
                aria-label="Trang chủ"
                data-testid="header-home"
              >
                <Home className="size-5" />
              </Link>
            )}
            {can('customer.read') && (
              <Link
                to="/viec-can-lam"
                className={cn('relative rounded-full p-2 text-muted-foreground hover:bg-muted hover:text-foreground', salesBar && 'max-md:hidden')}
                title={due ? `${due} việc cần làm hôm nay / quá hạn` : 'Việc cần làm'}
              >
                <ListTodo className="size-5" />
                {due > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex min-w-5 items-center justify-center rounded-full bg-error px-1 text-[11px] font-bold text-white ring-2 ring-card">
                    {due > 99 ? '99+' : due}
                  </span>
                )}
              </Link>
            )}
            {/* Tài xế, phụ kho: thanh dưới không có ô Bảng tin → nút ở đầu trang (anh Tiến 30/10/2026: ai chấm công cũng có Bảng tin) */}
            {(driverOnly || pickerOnly) && (
              <Link
                to="/bang-tin"
                className="rounded-full p-2 text-muted-foreground hover:bg-muted hover:text-foreground md:hidden"
                title="Bảng tin công ty"
                aria-label="Bảng tin công ty"
                data-testid="header-feed"
              >
                <Newspaper className="size-5" />
              </Link>
            )}
            {/* DEC-640: tài xế, kho, NV chấm công được chọn đăng bài: không có menu đầy đủ → nút ở đầu trang */}
            {(driverOnly || pickerOnly || staffOnly) && can('web_post.write') && menuAllowed(can, '/dang-bai-web') && (
              <Link
                to="/dang-bai-web"
                className="rounded-full p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
                title="Đăng bài và Ảnh lên web"
                aria-label="Đăng bài và Ảnh lên web"
                data-testid="header-web-post"
              >
                <Globe className="size-5" />
              </Link>
            )}
            <Link
              to="/huong-dan"
              className="rounded-full p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
              title="Hướng dẫn sử dụng"
              aria-label="Hướng dẫn sử dụng"
            >
              <BookOpen className="size-5" />
            </Link>
            {can('notification.read') && <NotificationBell />}

            <DM.Root>
              <DM.Trigger className="flex items-center gap-2 rounded-lg px-1.5 py-1 outline-none hover:bg-muted">
                <span className="flex size-10 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                  {initials(user?.fullName)}
                </span>
                <span className="hidden text-left leading-tight sm:block">
                  <span className="block text-sm font-semibold">{user?.fullName}</span>
                  <span className="block text-xs text-muted-foreground">{(user?.roleNames?.length ? user.roleNames : user?.roles.map((r) => ROLE_LABELS[r] ?? r))?.join(', ') || user?.username}</span>
                </span>
                <ChevronDown className="size-4 text-muted-foreground" />
              </DM.Trigger>
              <DM.Portal>
                <DM.Content align="end" sideOffset={6} className="z-50 min-w-48 rounded-lg border bg-card p-1 text-sm shadow-lg">
                  <div className="px-2 py-1.5 text-xs text-muted-foreground">Đăng nhập: {user?.username}</div>
                  <DM.Item
                    onSelect={() => setProfileOpen(true)}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 outline-none data-[highlighted]:bg-muted"
                  >
                    <IdCard className="size-4" /> Hồ sơ của tôi (SĐT, chữ ký, ảnh, giọng)
                  </DM.Item>
                  <DM.Item
                    onSelect={() => setPwOpen(true)}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 outline-none data-[highlighted]:bg-muted"
                  >
                    <KeyRound className="size-4" /> Đổi mật khẩu
                  </DM.Item>
                  <DM.Item asChild className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 outline-none data-[highlighted]:bg-muted">
                    <Link to="/huong-dan">
                      <BookOpen className="size-4" /> Hướng dẫn sử dụng
                    </Link>
                  </DM.Item>
                  {installMode !== 'installed' && (
                    <DM.Item
                      onSelect={() => setInstallOpen(true)}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 outline-none data-[highlighted]:bg-muted"
                    >
                      <Smartphone className="size-4" /> Cài ứng dụng lên điện thoại
                    </DM.Item>
                  )}
                  <DM.Separator className="my-1 h-px bg-border" />
                  <DM.Label className="px-2 py-1 text-xs text-muted-foreground">Giao diện</DM.Label>
                  <DM.RadioGroup value={themeMode} onValueChange={(v) => setThemeMode(v as ThemeMode)}>
                    {(Object.keys(THEME_LABELS) as ThemeMode[]).map((m) => {
                      const Icon = THEME_ICONS[m];
                      return (
                        <DM.RadioItem
                          key={m}
                          value={m}
                          onSelect={(e) => e.preventDefault()}
                          className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 outline-none data-[highlighted]:bg-muted data-[state=checked]:font-semibold data-[state=checked]:text-primary"
                        >
                          <Icon className="size-4" /> {THEME_LABELS[m]}
                        </DM.RadioItem>
                      );
                    })}
                  </DM.RadioGroup>
                  <DM.Separator className="my-1 h-px bg-border" />
                  <DM.Item
                    onSelect={() => void logout()}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-error outline-none data-[highlighted]:bg-muted"
                  >
                    <LogOut className="size-4" /> Đăng xuất
                  </DM.Item>
                </DM.Content>
              </DM.Portal>
            </DM.Root>
          </div>
        </header>
        {/* X-PWA bước 2: mất mạng / lượt chấm công chờ gửi */}
        <OfflineBanner />
        <main className="min-h-0 flex-1 overflow-auto">
          {/* Trang lỗi khi vẽ chỉ hiện thông báo trong khung nội dung, menu vẫn dùng được; đổi trang thì bỏ lỗi */}
          <ErrorBoundary resetKey={pathname}>
            {menuBlocked ? (
              <div className="p-4">
                <EmptyState title="Bạn không có quyền xem mục này" description="Cần quyền menu của mục này. Nhờ Quản trị tick ở Cài đặt → Nhân viên & phân quyền → Vị trí & quyền." />
              </div>
            ) : (
              <Outlet />
            )}
          </ErrorBoundary>
        </main>
        {/* Điện thoại: thanh điều hướng dưới cho các việc dùng nhiều nhất (Design System §7) */}
        <nav aria-label="Điều hướng nhanh" className="grid shrink-0 auto-cols-fr grid-flow-col border-t bg-card pb-[env(safe-area-inset-bottom)] md:hidden">
          {staffOnly ? (
            <StaffTabs pathname={pathname} />
          ) : pickerOnly ? (
            <PickerTabs pathname={pathname} pickCount={pickCount} />
          ) : driverOnly ? (
            <DriverTabs pathname={pathname} search={search} canReportSite={can('site.report')} />
          ) : (
            mobileTabs.map((t) => (
              <MobileTab key={t.to} icon={t.icon} label={t.label} to={t.to} active={t.match.test(pathname)} badge={t.to === '/soan-hang' ? pickCount : t.to === '/dt/viec' ? due : undefined} />
            ))
          )}
          {menuExtras?.length === 1 && (
            <MobileTab
              to={menuExtras[0]!.to}
              label={menuExtras[0]!.label}
              icon={menuExtras[0]!.icon}
              active={isLeafActive(menuExtras[0]!, here)}
            />
          )}
          {!staffOnly && !pickerOnly && !driverOnly && (!menuExtras || menuExtras.length > 1) && (
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="flex flex-col items-center gap-0.5 py-2 text-[11px] text-muted-foreground"
              aria-label="Mở menu"
            >
              <Menu className="size-5" /> Menu
            </button>
          )}
        </nav>
        <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
        <MyProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} />
        <InstallAppDialog open={installOpen} onClose={() => setInstallOpen(false)} />
        <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
        {/* CR-014: hộp thư khách hàng, tab mép phải mọi trang (Alt+Z) */}
        <InboxDrawer />
      </div>
    </div>
  );
}

type MobileTabDef = { to: string; label: string; icon: LucideIcon; perm?: Permission; anyPerm?: Permission[]; notPerm?: Permission; role?: string; notRole?: string; match: RegExp };
const MOBILE_TABS: MobileTabDef[] = [
  // X-PWA: điện thoại về màn mở đầu theo vai trò; Tổng quan vẫn ở menu
  { to: '/ung-dung', label: 'Trang chủ', icon: Home, match: /^\/(ung-dung)?$/ },
  // Anh Tiến 20/10/2026: Thủ kho có nút Soạn hàng ở thanh dưới (người bán hàng / quản trị giữ thanh dưới như cũ)
  { to: '/soan-hang', label: 'Soạn hàng', icon: PackageCheck, perm: 'delivery.pick', notPerm: 'quote.read', match: /^\/soan-hang/ },
  { to: '/bao-gia', label: 'Báo giá', icon: FileText, perm: 'quote.read', match: /^\/bao-gia(\/|$)/ },
  { to: '/bao-gia-nhanh', label: 'BG nhanh', icon: Zap, perm: 'quote.create', match: /^\/bao-gia-nhanh/ },
  // Anh Tiến 21/10/2026: NV Kiểm soát (vai trò controller) có ô "Đơn Asia" (= Đơn hàng Asia, /phieu-xuat) thay ô Dự án; Dự án vẫn ở menu
  { to: '/phieu-xuat', label: 'Đơn Asia', icon: FileSignature, perm: 'sales_order.read', role: 'controller', match: /^\/phieu-xuat/ },
  // CR-016: báo dự án công trình (DEC-369: mọi nhân viên)
  { to: '/du-an', label: 'Dự án', icon: HardHat, perm: 'site.report', notRole: 'controller', match: /^\/du-an/ },
  { to: '/khach-hang', label: 'Khách hàng', icon: Users, perm: 'customer.read', match: /^\/khach-hang/ },
  // P12 B2: tài xế mở nhanh màn nhận xe / đổ dầu
  { to: '/tai-xe', label: 'Tài xế', icon: Fuel, perm: 'fleet.driver', match: /^\/tai-xe/ },
];

/**
 * Chân trang điện thoại cho Kinh doanh, Mua hàng, Trưởng bộ phận, Giám đốc khu vực (anh Tiến 02/11/2026):
 * Báo giá (gồm Báo giá nhanh) · Đơn Asia (SO3 hôm qua / hôm nay / ngày mai) · Giao hàng · Lead · Việc của tôi; Trang chủ ở đầu trang,
 * Khách hàng, Dự án… vào qua Menu. Mục nào không có quyền thì ẩn.
 */
const SALES_TABS: MobileTabDef[] = [
  { to: '/dt/bao-gia', label: 'Báo giá', icon: FileText, perm: 'quote.read', match: /^\/(dt\/bao-gia|bao-gia)/ },
  { to: '/dt/don-asia', label: 'Đơn Asia', icon: FileSignature, perm: 'sales_order.read', match: /^\/(dt\/don-asia|phieu-xuat)/ },
  { to: '/dt/giao-hang', label: 'Giao hàng', icon: Truck, anyPerm: ['sales_order.read', 'delivery.read_all'], match: /^\/(dt\/giao-hang|van-chuyen)/ },
  { to: '/dt/lead', label: 'Lead', icon: UserPlus, perm: 'lead.read', match: /^\/(dt\/lead|lead)(\/|$)/ },
  { to: '/dt/viec', label: 'Việc của tôi', icon: ListChecks, match: /^\/(dt\/viec|viec-can-lam|viec-cua-toi)/ },
];

/** CTV (DEC-375, anh Tiến 19/10/2026): Trang chủ (Bảng tin, ô lớn) / Khách hàng / Đơn hàng của tôi / Doanh số & hoa hồng. */
const CTV_TABS: typeof MOBILE_TABS = [
  { to: '/ung-dung', label: 'Trang chủ', icon: Home, match: /^\/(ung-dung)?$/ },
  { to: '/khach-hang', label: 'Khách hàng', icon: Users, perm: 'customer.read', match: /^\/khach-hang/ },
  { to: '/don-cua-toi', label: 'Đơn hàng', icon: PackageCheck, perm: 'quote.read', match: /^\/don-cua-toi/ },
  { to: '/thu-nhap', label: 'Hoa hồng', icon: HandCoins, perm: 'quote.read', match: /^\/thu-nhap/ },
];

/** NV chỉ chấm công (vai trò staff): menu chỉ 3 mục (mẫu gửi anh Tiến 13/10/2026). */
const STAFF_NAV: NavEntry[] = [
  { to: '/cham-cong?tab=me', label: 'Chấm công', icon: Clock, match: /^\/cham-cong/ },
  { to: '/cong-nhan-vien', label: 'Của tôi', icon: UserRound },
  // DEC-369: mọi nhân viên báo dự án công trình (có thưởng)
  { to: '/du-an', label: 'Dự án công trình', icon: HardHat, match: /^\/du-an/ },
  { to: '/bang-tin', label: 'Bảng tin', icon: Newspaper },
  { to: '/huong-dan', label: 'Hướng dẫn', icon: BookOpen },
];

/** Phụ kho: menu chỉ Soạn hàng, Chấm công, Của tôi, Bảng tin, Hướng dẫn (anh Tiến 14/10/2026). */
const PICKER_NAV: NavEntry[] = [
  { to: '/soan-hang', label: 'Soạn hàng', icon: PackageCheck },
  { to: '/cham-cong?tab=me', label: 'Chấm công', icon: Clock, match: /^\/cham-cong/ },
  { to: '/cong-nhan-vien', label: 'Của tôi', icon: UserRound },
  // DEC-369: mọi nhân viên báo dự án công trình (có thưởng)
  { to: '/du-an', label: 'Dự án công trình', icon: HardHat, match: /^\/du-an/ },
  { to: '/bang-tin', label: 'Bảng tin', icon: Newspaper },
  { to: '/huong-dan', label: 'Hướng dẫn', icon: BookOpen },
];

/** Phụ kho: thanh dưới Soạn hàng / Chấm công / Của tôi / Dự án (Hướng dẫn và chuông ở đầu trang). */
function PickerTabs({ pathname, pickCount }: { pathname: string; pickCount: number }) {
  const sum = useEssSummary();
  return (
    <>
      <MobileTab to="/soan-hang" label="Soạn hàng" icon={PackageCheck} active={pathname.startsWith('/soan-hang')} badge={pickCount} />
      <MobileTab to="/cham-cong?tab=me" label="Chấm công" icon={Clock} active={pathname.startsWith('/cham-cong')} />
      <MobileTab to="/cong-nhan-vien" label="Của tôi" icon={UserRound} active={pathname.startsWith('/cong-nhan-vien')} badge={sum.data?.unreadPolicies} />
      <MobileTab to="/du-an" label="Dự án" icon={HardHat} active={pathname.startsWith('/du-an')} />
    </>
  );
}

/** NV chỉ chấm công: thanh dưới 4 tab (Hướng dẫn nằm ở đầu trang; Dự án từ DEC-369); "Của tôi" (Cổng NV) hiện số nội quy chưa đọc. */
function StaffTabs({ pathname }: { pathname: string }) {
  const sum = useEssSummary();
  return (
    <>
      <MobileTab to="/cham-cong?tab=me" label="Chấm công" icon={Clock} active={pathname.startsWith('/cham-cong')} />
      <MobileTab to="/cong-nhan-vien" label="Của tôi" icon={UserRound} active={pathname.startsWith('/cong-nhan-vien')} badge={sum.data?.unreadPolicies} />
      <MobileTab to="/bang-tin" label="Bảng tin" icon={Newspaper} active={pathname.startsWith('/bang-tin')} />
      {/* DEC-369: mọi nhân viên báo dự án công trình (có thưởng) */}
      <MobileTab to="/du-an" label="Dự án" icon={HardHat} active={pathname.startsWith('/du-an')} />
    </>
  );
}

/**
 * Tài khoản chỉ có vai trò Tài xế – anh Tiến 20/10/2026: thanh dưới 4 tab chữ to. Giao hàng, Xe & dầu, Tháng xe có vạch xanh (việc công ty);
 * tab Công vạch vàng (phần người lao động, mở màn Cổng NV như cũ; anh Tiến: "CÔNG"). Thông báo, hướng dẫn, tài khoản ở đầu trang.
 * Tab Dự án (anh Tiến 22/10/2026 "Đưa tab Dự án xuống menu màn hình tài xế"): mở thẳng bản báo nhanh, thay nút vàng cuối trang Giao hàng.
 */
function DriverTabs({ pathname, search, canReportSite }: { pathname: string; search: string; canReportSite: boolean }) {
  const muc = pathname === '/tai-xe' ? new URLSearchParams(search).get('muc') : undefined;
  return (
    <>
      <MobileTab to="/tai-xe" label="Giao hàng" icon={Truck} stripe="company" big active={pathname === '/tai-xe' && !muc} />
      <MobileTab to="/tai-xe?muc=xe" label="Xe & dầu" icon={Fuel} stripe="company" big active={muc === 'xe'} />
      <MobileTab to="/tai-xe?muc=thang" label="Tháng xe" icon={CalendarDays} stripe="company" big active={muc === 'thang'} />
      {canReportSite && <MobileTab to="/du-an/bao?nhanh=1" label="Dự án" icon={HardHat} stripe="company" big active={pathname.startsWith('/du-an')} />}
      <MobileTab to="/cong-nhan-vien" label="Công" icon={UserRound} stripe="me" big active={/^\/(cong-nhan-vien|cham-cong)/.test(pathname)} />
    </>
  );
}

function MobileTab({ to, label, icon: Icon, active, badge, stripe, big }: { to: string; label: string; icon: LucideIcon; active: boolean; badge?: number; stripe?: 'company' | 'me'; big?: boolean }) {
  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex flex-col items-center gap-0.5 py-2',
        big ? 'text-[13px]' : 'text-[11px]',
        // Tài xế: vạch trên xanh = việc công ty, vàng = Cổng NV (phần người lao động)
        stripe && 'border-t-4',
        stripe === 'company' && 'border-info',
        stripe === 'me' && 'border-warning',
        active ? (big ? 'bg-accent font-bold text-primary' : 'font-semibold text-primary') : 'font-medium text-foreground/80',
      )}
    >
      <span className="relative">
        <Icon className={big ? 'size-6' : 'size-5'} />
        {!!badge && (
          <span className="absolute -right-2.5 -top-1.5 min-w-4 rounded-full bg-destructive px-1 text-center text-[10px] font-semibold leading-4 text-white">{badge > 99 ? '99+' : badge}</span>
        )}
      </span>
      {label}
    </Link>
  );
}

/** Mục đang mở: nền sidebar-active + vạch trái Brand Green (Design System §16), không tô đặc cả mục. */
const ACTIVE_ITEM = 'bg-sidebar-active font-semibold text-white shadow-[inset_3px_0_0_var(--sidebar-accent)]';

function TopLink({ leaf, active, rail }: { leaf: NavLeaf; active: boolean; rail?: boolean }) {
  return (
    <Link
      to={leaf.to}
      title={rail ? leaf.label : undefined}
      aria-label={rail ? leaf.label : undefined}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex items-center rounded-lg text-[15px] transition-colors',
        rail ? 'size-10 justify-center' : 'gap-3 px-3 py-2.5',
        active ? ACTIVE_ITEM : 'hover:bg-sidebar-hover',
        leaf.soon && !active && 'text-sidebar-foreground/75',
      )}
    >
      <leaf.icon className={cn('size-5 shrink-0', active && 'text-sidebar-accent')} />
      {!rail && <span className="flex-1 truncate">{leaf.label}</span>}
      {!rail && leaf.to === '/tro-ly-ai' && <span className="rounded bg-primary px-1.5 text-[11px] font-bold text-primary-foreground">AI</span>}
      {!rail && leaf.soon && <SoonDot />}
    </Link>
  );
}

function RailButton({ icon: Icon, label, active, onClick }: { icon: NavGroup['icon']; label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      className={cn('flex size-10 items-center justify-center rounded-lg transition-colors', active ? ACTIVE_ITEM : 'hover:bg-sidebar-hover')}
    >
      <Icon className={cn('size-5 shrink-0', active && 'text-sidebar-accent')} />
    </button>
  );
}

function Group({
  group,
  path,
  due,
  overdue,
  open,
  onToggle,
}: {
  group: NavGroup;
  path: string;
  due: number;
  overdue: boolean;
  open: boolean | undefined;
  onToggle: (k: string, v: boolean) => void;
}) {
  const containsActive = group.children.some((c) => isLeafActive(c, path));
  // Mặc định mở nhóm chứa trang hiện tại; người dùng tự đóng/mở thì nhớ lại
  const expanded = open ?? containsActive;
  return (
    <div className="rounded-lg">
      <button
        onClick={() => onToggle(group.key, !expanded)}
        className={cn(
          'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] transition-colors',
          containsActive ? ACTIVE_ITEM : 'hover:bg-sidebar-hover',
        )}
        aria-expanded={expanded}
      >
        <group.icon className={cn('size-5 shrink-0', containsActive && 'text-sidebar-accent')} />
        <span className="flex-1 text-left">{group.label}</span>
        <ChevronDown className={cn('size-4 transition-transform', !expanded && '-rotate-90')} />
      </button>
      {expanded && (
        <div className="relative my-1 ml-5 border-l border-white/15 pl-2">
          {group.children.map((c, i) => {
            const active = isLeafActive(c, path);
            const heading = c.section && c.section !== group.children[i - 1]?.section ? c.section : null;
            return (
              <Fragment key={c.to}>
                {heading && <div className="px-2.5 pb-0.5 pt-2 text-[11px] font-semibold uppercase tracking-wide text-sidebar-accent">{heading}</div>}
                <Link
                  to={c.to}
                  className={cn(
                    'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors',
                    active
                      ? 'bg-white/10 font-semibold text-white'
                      : c.soon
                        ? 'text-sidebar-foreground/65 hover:bg-sidebar-hover hover:text-white'
                        : 'text-sidebar-foreground/90 hover:bg-sidebar-hover hover:text-white',
                  )}
                >
                  <c.icon className={cn('size-4 shrink-0', active ? 'text-sidebar-accent' : 'opacity-80')} />
                  <span className="flex-1 truncate">{c.label}</span>
                  {c.badge === 'followups' && due > 0 && (
                    <span className={cn('rounded-full px-1.5 text-[11px] font-bold text-white', overdue ? 'bg-error' : 'bg-warning')}>{due}</span>
                  )}
                  {c.soon && <SoonDot />}
                </Link>
              </Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}

const SoonDot = () => <span title="Sắp có – chưa có dữ liệu" className="size-1.5 shrink-0 rounded-full bg-warning" />;

const initials = (name?: string) =>
  (name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(-2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
